import React from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Text } from 'grommet';
import { MetricCard } from '@Components/Partial/Product/Analytics';
import {
  StyledPanel,
  StyledScrollWrap,
  StyledTable,
} from './StyledCidAnalyticsDashboardPage';
import {
  EST_REVENUE_COLORS,
  formatMoney, formatMonthLabel,
  PanelTitle, ColorDot, EmptyTable,
} from './CidAnalyticsShared';
import { EstimatedRevenueAreaChart } from './CidAnalyticsCharts';

export default function CidAnalyticsEstimatedTab({
  estTotals,
  estByMonth,
  estByCreator,
  loading,
  page,
  small,
  hasFilters,
}) {
  return (
    <Box gap="1rem">

      {/* Estimated KPI Cards */}
      <Grid columns={small ? ['1fr'] : ['1fr', '1fr', '1fr']} gap="1rem">
        <MetricCard
          label="TOTAL EST. REVENUE"
          value={estTotals ? formatMoney(estTotals.totalEstimatedRevenue) : 'Data unavailable'}
          subLabel={hasFilters ? 'selected period' : 'all time'}
          textColor={EST_REVENUE_COLORS.totalEstimatedRevenue}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={EST_REVENUE_COLORS.totalEstimatedRevenue}
        />
        <MetricCard
          label="EST. REDFLAG SHARE"
          value={estTotals ? formatMoney(estTotals.estimatedRedflagShare) : 'Data unavailable'}
          subLabel={hasFilters ? 'selected period' : 'all time'}
          textColor={EST_REVENUE_COLORS.estimatedRedflagShare}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={EST_REVENUE_COLORS.estimatedRedflagShare}
        />
        <MetricCard
          label="EST. PAYABLE REVENUE"
          value={estTotals ? formatMoney(estTotals.estimatedPayableRevenue) : 'Data unavailable'}
          subLabel={hasFilters ? 'selected period' : 'all time'}
          textColor={EST_REVENUE_COLORS.estimatedPayableRevenue}
          subLabelColor={page.secondaryText}
          cardAccent={page.altBg}
          loading={loading}
          borderColor={EST_REVENUE_COLORS.estimatedPayableRevenue}
        />
      </Grid>

      {/* Estimated Revenue Area Chart */}
      <StyledPanel gap="0.75rem">
        <PanelTitle label="Estimated Revenue per Month" />
        <Box direction="row" gap="1rem" align="center" flex={false}>
          <Box direction="row" align="center" gap="0.35rem">
            <ColorDot color={EST_REVENUE_COLORS.totalEstimatedRevenue} />
            <Text size="0.75rem" color={page.secondaryText}>Est. Total Revenue</Text>
          </Box>
          <Box direction="row" align="center" gap="0.35rem">
            <ColorDot color={EST_REVENUE_COLORS.estimatedRedflagShare} />
            <Text size="0.75rem" color={page.secondaryText}>Est. Redflag Share</Text>
          </Box>
          <Box direction="row" align="center" gap="0.35rem">
            <ColorDot color={EST_REVENUE_COLORS.estimatedPayableRevenue} />
            <Text size="0.75rem" color={page.secondaryText}>Est. Payable Revenue</Text>
          </Box>
        </Box>
        <EstimatedRevenueAreaChart data={estByMonth} loading={loading} />
      </StyledPanel>

      {/* Two-column: Est. Revenue by Month + Est. Revenue per Creator */}
      <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">

        <StyledPanel gap="0.75rem">
          <PanelTitle label="Estimated Revenue by Month" />
          {estByMonth && estByMonth.length > 0 ? (
            <StyledScrollWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Month</th>
                    <th>Est. Total</th>
                    <th>Redflag Share</th>
                    <th>Payable</th>
                  </tr>
                </thead>
                <tbody>
                  {estByMonth.map((row) => (
                    <tr key={row.month}>
                      <td>{formatMonthLabel(row.month)}</td>
                      <td style={{ color: EST_REVENUE_COLORS.totalEstimatedRevenue, fontWeight: 600 }}>
                        {formatMoney(row.totalEstimatedRevenue)}
                      </td>
                      <td style={{ color: EST_REVENUE_COLORS.estimatedRedflagShare }}>
                        {formatMoney(row.estimatedRedflagShare)}
                      </td>
                      <td style={{ color: EST_REVENUE_COLORS.estimatedPayableRevenue }}>
                        {formatMoney(row.estimatedPayableRevenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollWrap>
          ) : <EmptyTable page={page} loading={loading} />}
        </StyledPanel>

        <StyledPanel gap="0.75rem">
          <PanelTitle label="Estimated Revenue per Creator" />
          {estByCreator && estByCreator.length > 0 ? (
            <StyledScrollWrap>
              <StyledTable>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', width: '40px' }}>#</th>
                    <th style={{ textAlign: 'left' }}>Asset Label</th>
                    <th>Est. Total</th>
                    <th>Redflag Share</th>
                    <th>Payable</th>
                  </tr>
                </thead>
                <tbody>
                  {estByCreator.map((row, i) => (
                    <tr key={row.assetLabel}>
                      <td style={{ textAlign: 'left', color: 'var(--page-hint-text)' }}>{i + 1}</td>
                      <td style={{ textAlign: 'left' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                          {row.assetLabel}
                          <span style={{
                            fontSize: '0.65rem',
                            fontWeight: 600,
                            padding: '1px 5px',
                            borderRadius: '3px',
                            background: row.isActive ? '#16a34a' : '#6b7280',
                            color: '#fff',
                            letterSpacing: '0.02em',
                            whiteSpace: 'nowrap',
                          }}
                          >
                            {row.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </span>
                      </td>
                      <td style={{ color: EST_REVENUE_COLORS.totalEstimatedRevenue, fontWeight: 600 }}>
                        {formatMoney(row.totalEstimatedRevenue)}
                      </td>
                      <td style={{ color: EST_REVENUE_COLORS.estimatedRedflagShare }}>
                        {formatMoney(row.estimatedRedflagShare)}
                      </td>
                      <td style={{ color: EST_REVENUE_COLORS.estimatedPayableRevenue }}>
                        {formatMoney(row.estimatedPayableRevenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollWrap>
          ) : <EmptyTable page={page} loading={loading} />}
        </StyledPanel>

      </Grid>

    </Box>
  );
}

CidAnalyticsEstimatedTab.propTypes = {
  estTotals: PropTypes.shape({
    totalEstimatedRevenue: PropTypes.number,
    estimatedRedflagShare: PropTypes.number,
    estimatedPayableRevenue: PropTypes.number,
  }),
  estByMonth: PropTypes.array,
  estByCreator: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  page: PropTypes.object.isRequired,
  small: PropTypes.bool.isRequired,
  hasFilters: PropTypes.bool.isRequired,
};
